import {
  IconBasketDiscount,
  IconCurrencyDollar,
  IconLock,
  IconTicket,
  IconWalk,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import {
  ContentCard,
  Leaderboard,
  MetricCard,
  ScalrBoxView,
  ScalrCarousel,
  ScalrTableColumn,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import KPIGoalChart from '@/components/ui/charts/KpiGoalChart'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { IAbstractRegionMetric } from '@/types/common'

// Mock data for the locked page
const mockMetricCardData = {
  adns: 2150.5,
  traffic: 1250,
  avgCheck: 18.75,
  totalDiscountsRatio: 12.5,
}

const mockItemCountMixData = [
  {
    id: 'Coffee Latte',
    name: 'Coffee Latte',
    itemMixPercentage: 25.4,
    count: 145,
  },
  { id: 'Cappuccino', name: 'Cappuccino', itemMixPercentage: 18.2, count: 104 },
  { id: 'Americano', name: 'Americano', itemMixPercentage: 15.8, count: 90 },
  { id: 'Espresso', name: 'Espresso', itemMixPercentage: 12.1, count: 69 },
  { id: 'Macchiato', name: 'Macchiato', itemMixPercentage: 8.9, count: 51 },
]

const mockItemCountOrderedMixData = [
  {
    id: 'Iced Coffee',
    name: 'Iced Coffee',
    itemMixPercentage: 22.1,
    count: 126,
  },
  { id: 'Cold Brew', name: 'Cold Brew', itemMixPercentage: 19.3, count: 110 },
  {
    id: 'Frappuccino',
    name: 'Frappuccino',
    itemMixPercentage: 16.7,
    count: 95,
  },
]

const mockModifierCountMixData = [
  { id: 'Extra Shot', name: 'Extra Shot', orderPenetration: 35.2, count: 201 },
  { id: 'Oat Milk', name: 'Oat Milk', orderPenetration: 28.9, count: 165 },
  { id: 'Extra Hot', name: 'Extra Hot', orderPenetration: 22.4, count: 128 },
]

const mockRegionBoxData: IAbstractRegionMetric<any>[] = [
  { region: 'Downtown', stand: 'da', current: 15420, prev: 14000 },
  { region: 'Mall', stand: 'da', current: 12350, prev: 11000 },
  { region: 'Airport', stand: 'da', current: 18750, prev: 17000 },
]

const mockCategoryCountMixOptions = {
  tooltip: { trigger: 'item' },
  series: [
    {
      name: 'Category Mix',
      type: 'pie',
      radius: '50%',
      data: [
        { value: 45, name: 'Hot Drinks' },
        { value: 30, name: 'Cold Drinks' },
        { value: 15, name: 'Pastries' },
        { value: 10, name: 'Snacks' },
      ],
    },
  ],
}

const mockSalesInAWeekOptions = {
  xAxis: {
    type: 'category',
    data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  },
  yAxis: { type: 'value' },
  series: [
    {
      data: [2200, 1800, 2100, 2400, 2800, 3200, 2900],
      type: 'bar',
    },
  ],
}

const mockColumns: ScalrTableColumn[] = [
  { accessorKey: 'name', header: 'Item', type: 'text' },
  { accessorKey: 'itemMixPercentage', header: 'Mix %', type: 'percentage' },
  { accessorKey: 'count', header: 'Count', type: 'number' },
]

const mockModifierColumns: ScalrTableColumn[] = [
  { accessorKey: 'name', header: 'Modifier', type: 'text' },
  {
    accessorKey: 'orderPenetration',
    header: 'Penetration %',
    type: 'percentage',
  },
  { accessorKey: 'count', header: 'Count', type: 'number' },
]

export default function NotAllowedClarityPage({
  pageTitle,
}: {
  pageTitle: string
}) {
  const { t } = useTranslation()

  return (
    <div className="relative">
      {/* Mock Sales Page Content - Blurred */}
      <div className="blur-sm pointer-events-none select-none">
        <div className="space-y-6">
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              <MetricCard
                title={t('clarity.metrics.adns')}
                content={mockMetricCardData.adns.toFixed(2)}
                type="money"
                subTitle={t('clarity.metrics.adnsSubtitle')}
                loading={false}
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.red500}
              />
              <MetricCard
                title={t('clarity.metrics.traffic')}
                content={mockMetricCardData.traffic.toFixed(0)}
                type="number"
                loading={false}
                icon={<IconWalk color="white" size={20} />}
                color={metricCardColors.blue500}
              />
              <MetricCard
                title={t('clarity.metrics.aov')}
                content={mockMetricCardData.avgCheck.toFixed(2)}
                type="money"
                subTitle={t('clarity.metrics.aovSubtitle')}
                loading={false}
                icon={<IconTicket color="white" size={20} />}
                color={metricCardColors.green500}
              />
              <MetricCard
                title={t('clarity.metrics.discounts')}
                content={mockMetricCardData.totalDiscountsRatio.toFixed(0)}
                type="percentage"
                loading={false}
                icon={<IconBasketDiscount color="white" size={20} />}
                color={metricCardColors.orange500}
                isIncreasingPositive={false}
              />
            </ScalrCarousel>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Leaderboard
              title={t('clarity.cards.bestSellers')}
              data={mockItemCountMixData}
              columns={mockColumns}
              sortBy="itemMixPercentage"
              loading={false}
            />

            <Leaderboard
              title={t('clarity.cards.whatsTrending')}
              data={mockItemCountOrderedMixData}
              columns={mockColumns}
              sortBy="itemMixPercentage"
              loading={false}
            />

            <ContentCard
              title={t('clarity.cards.categoryCountMix')}
              onViewMore={() => {}}
              loading={false}
            >
              <ReactEcharts option={mockCategoryCountMixOptions} />
            </ContentCard>
          </div>

          <ScalrBoxView
            data={mockRegionBoxData}
            loading={false}
            filterValues={{
              start: '2024-01-01',
              end: '2024-01-31',
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ContentCard
              title={t('clarity.cards.salesInAWeek')}
              onViewMore={() => {}}
              loading={false}
            >
              <ReactEcharts option={mockSalesInAWeekOptions} />
            </ContentCard>

            <Leaderboard
              title={t('clarity.cards.bestModifiers')}
              data={mockModifierCountMixData}
              columns={mockModifierColumns}
              sortBy="orderPenetration"
              loading={false}
            />

            <ContentCard
              title={t('clarity.cards.adnsVsKpiGoal')}
              loading={false}
            >
              <KPIGoalChart
                trailingGoalData={2250.0}
                currentData={2150.5}
                kpiGoalData={2400.0}
                unit="money"
              />
            </ContentCard>
          </div>
        </div>
      </div>

      {/* Lock Overlay */}
      <div className="absolute inset-0 flex bg-background/60 backdrop-blur-sm">
        <div className="text-center space-y-6 max-w-md mx-auto p-8">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-muted">
              <IconLock size={48} className="text-muted-foreground" />
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              {t('errors.locked.heading', { page: pageTitle })}
            </h2>
            <p className="text-muted-foreground">
              {t('errors.locked.message')}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                window.open('https://www.scalrplatform.com/about-us', '_blank')
              }}
            >
              {t('errors.locked.upgradePlan')}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={() => {
                window.open('https://www.scalrplatform.com/', '_blank')
              }}
            >
              {t('errors.locked.learnMore')}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {t('errors.locked.contactAdmin')}
          </p>
        </div>
      </div>
    </div>
  )
}
